import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TextField,
  MenuItem,
  Stack,
  Button,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import { listPassagens, getPassagensFiltersMeta, exportPassagensCsv } from "../../api/passagensApi";
import { FEATURE_PASSAGENS_REPASSES_ENABLED } from "../../constants/featureFlags";
import PassagemDetailDrawer from "./PassagemDetailDrawer";
import PassengerPaymentCell from "./passagemMpPaymentUi";
import PassengerSaleValueCell from "../Platform/XprobaseConsole/PassengerSaleValueCell";
import { passagemStatusLabel, passagemListStatusFilterKeys } from "./passagemStatusUi";

function defaultMonthRange() {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  return { start: fmt(start), end: fmt(end) };
}

const STATUS_COLOR = {
  emitida: "success",
  paga: "error",
  cancelada: "default",
  estornada: "warning",
  chargeback: "error",
  aguardando_pagamento: "warning",
  em_andamento: "default",
  erro_emissao: "error",
};

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDateTime(v) {
  if (!v) return "—";
  return new Date(v).toLocaleString("pt-BR");
}

export default function PassagensListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailId, setDetailId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const defaults = useMemo(() => defaultMonthRange(), []);

  const filters = useMemo(
    () => ({
      start: searchParams.get("start") || defaults.start,
      end: searchParams.get("end") || defaults.end,
      status: searchParams.get("status") || "",
      id_origem: searchParams.get("canal") || "",
      search: searchParams.get("q") || "",
      limit: Number(searchParams.get("limit") || 50),
      offset: Number(searchParams.get("offset") || 0),
    }),
    [searchParams, defaults]
  );

  useEffect(() => {
    if (searchParams.get("start") && searchParams.get("end")) return;
    const next = new URLSearchParams(searchParams);
    if (!searchParams.get("start")) next.set("start", defaults.start);
    if (!searchParams.get("end")) next.set("end", defaults.end);
    setSearchParams(next, { replace: true });
  }, [defaults.end, defaults.start, searchParams, setSearchParams]);

  const setFilter = useCallback(
    (key, value) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set(key, value);
      else next.delete(key);
      next.set("offset", "0");
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const { data: meta } = useQuery(["passagens-filters-meta"], getPassagensFiltersMeta, {
    staleTime: 5 * 60 * 1000,
  });

  const { data, isLoading, isFetching, refetch, error } = useQuery(
    ["passagens-list", filters],
    () => listPassagens(filters),
    { keepPreviousData: true }
  );

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const attentionEmissionCount = useMemo(
    () =>
      rows.filter((r) => {
        const s = String(r.status_venda || "").toLowerCase();
        return s === "paga" || s === "erro_emissao";
      }).length,
    [rows]
  );
  const isChargebackView =
    filters.status === "chargeback" || data?.appliedDateField === "chargeback";

  const statusOptions = useMemo(() => passagemListStatusFilterKeys(), []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportPassagensCsv(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "passagens.csv";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ConfirmationNumberIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            {t("passagensPage.title", { defaultValue: "Passagens vendidas" })}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {FEATURE_PASSAGENS_REPASSES_ENABLED ? (
            <Button
              size="small"
              variant="outlined"
              component={RouterLink}
              to="/passagens/repasses"
            >
              {t("passagensRepasses.title", { defaultValue: "Repasses" })}
            </Button>
          ) : null}
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewKanbanIcon />}
            onClick={() => navigate("/passagens/pipeline")}
          >
            {t("passagensPage.pipelineView", { defaultValue: "Pipeline" })}
          </Button>
          <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport} disabled={exporting}>
            {t("passagensPage.export", { defaultValue: "Exportar CSV" })}
          </Button>
          <IconButton onClick={() => refetch()} disabled={isFetching}>
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
          <TextField
            size="small"
            type="date"
            label={
              isChargebackView
                ? t("passagensPage.filterFromChargeback", { defaultValue: "Contestação de" })
                : t("passagensPage.filterFrom", { defaultValue: "De" })
            }
            value={filters.start}
            onChange={(e) => setFilter("start", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label={
              isChargebackView
                ? t("passagensPage.filterToChargeback", { defaultValue: "Contestação até" })
                : t("passagensPage.filterTo", { defaultValue: "Até" })
            }
            value={filters.end}
            onChange={(e) => setFilter("end", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            size="small"
            label={t("passagensPage.filterStatus", { defaultValue: "Status" })}
            value={filters.status}
            onChange={(e) => setFilter("status", e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t("common.all", { defaultValue: "Todos" })}</MenuItem>
            {statusOptions.map((s) => (
              <MenuItem key={s} value={s}>
                {passagemStatusLabel(t, s)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t("passagensPage.filterChannel", { defaultValue: "Canal" })}
            value={filters.id_origem}
            onChange={(e) => setFilter("canal", e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t("common.all", { defaultValue: "Todos" })}</MenuItem>
            {(meta?.canais || []).map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.nome}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label={t("passagensPage.search", { defaultValue: "Buscar" })}
            value={filters.search}
            onChange={(e) => setFilter("q", e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
          />
        </Stack>
        {isChargebackView ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t("passagensPage.chargebackDateFilterHint", {
              defaultValue:
                "Com status Chargeback, o período De/Até usa a data da contestação (não a emissão da passagem).",
            })}
          </Alert>
        ) : null}
      </Paper>

      {error ? (
        <Alert severity="error">{error?.message || String(error)}</Alert>
      ) : null}

      {attentionEmissionCount > 0 ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {t("passagensPage.attentionEmissionAlert", {
            ns: "app",
            count: attentionEmissionCount,
            defaultValue:
              "{{count}} venda(s) paga(s) sem BPe emitido no período — precisam de atenção.",
          })}
        </Alert>
      ) : null}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {isChargebackView
                  ? t("passagensPage.colChargebackDate", { defaultValue: "Contestação" })
                  : t("passagensPage.colDate", { defaultValue: "Emissão" })}
              </TableCell>
              <TableCell>{t("passagensPage.colClient", { defaultValue: "Cliente" })}</TableCell>
              <TableCell>{t("passagensPage.colRoute", { defaultValue: "Rota" })}</TableCell>
              <TableCell>{t("passagensPage.colValue", { defaultValue: "Valor" })}</TableCell>
              <TableCell>{t("passagensPage.colStatus", { defaultValue: "Status" })}</TableCell>
              <TableCell>{t("passagensPage.colPayment", { defaultValue: "Pagamento" })}</TableCell>
              <TableCell>{t("passagensPage.colChannel", { defaultValue: "Canal" })}</TableCell>
              <TableCell>{t("passagensPage.colPartner", { defaultValue: "Parceiro" })}</TableCell>
              <TableCell>{t("passagensPage.colLocator", { defaultValue: "Localizador" })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length ? (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setDetailId(row.id)}
                >
                  <TableCell>
                    {fmtDateTime(
                      isChargebackView
                        ? row.chargeback_em
                        : row.emitido_em || row.criado_em
                    )}
                  </TableCell>
                  <TableCell>{row.client_name || "—"}</TableCell>
                  <TableCell>
                    {[row.origem, row.destino].filter(Boolean).join(" → ") || "—"}
                  </TableCell>
                  <TableCell align="right">
                    <PassengerSaleValueCell row={row} tenantView />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={passagemStatusLabel(t, row.status_venda)}
                      color={STATUS_COLOR[row.status_venda] || "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <PassengerPaymentCell row={row} t={t} />
                  </TableCell>
                  <TableCell>{row.channel_name || row.canal_source || "—"}</TableCell>
                  <TableCell>{row.partner_key || "—"}</TableCell>
                  <TableCell>{row.localizador || row.numero_bpe || "—"}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  {t("passagensPage.empty", { defaultValue: "Nenhuma passagem encontrada." })}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {total} {t("passagensPage.totalLabel", { defaultValue: "registros" })}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            disabled={filters.offset <= 0}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.set("offset", String(Math.max(0, filters.offset - filters.limit)));
              setSearchParams(next);
            }}
          >
            {t("common.previous", { defaultValue: "Anterior" })}
          </Button>
          <Button
            size="small"
            disabled={filters.offset + filters.limit >= total}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.set("offset", String(filters.offset + filters.limit));
              setSearchParams(next);
            }}
          >
            {t("common.next", { defaultValue: "Próximo" })}
          </Button>
        </Stack>
      </Stack>

      <PassagemDetailDrawer
        open={detailId != null}
        passagemId={detailId}
        onClose={() => setDetailId(null)}
      />
    </Box>
  );
}
